// ---------------------------------------------------------------------------
// dragScroll — arrastar carrossel horizontal com o MOUSE.
//
// ## O problema
//
// Todo carrossel do app é um scroller nativo (`overflow-x-auto`) com a barra
// escondida (`scrollbar-hide` ou `[&::-webkit-scrollbar]:hidden`). No celular
// isso é perfeito: o dedo arrasta. No desktop não sobra affordance nenhuma —
// o mouse não arrasta scroller nativo, e a barra que faria o papel está
// escondida por decisão de design. Medido a 1440px num rail da Home
// (`scrollWidth` 1490 contra `clientWidth` 430): arrastar 150px com o mouse
// mexia o `scrollLeft` em ZERO. O conteúdo existia e não tinha como ser
// alcançado.
//
// ## Por que um listener delegado, e não um hook por carrossel
//
// São 25 scrollers horizontais espalhados por 25 arquivos (Home, perfil,
// estante, loja, busca, Studio, Foco, insights, admin…). Um hook por call site
// resolve os 25 de hoje e deixa o 26º nascer quebrado — que é exatamente como
// a busca do onboarding divergiu da `/search` (BUG-81). Aqui o gate é o módulo:
// um listener na raiz cobre todo scroller horizontal que existe e todo que
// vier, sem ninguém precisar lembrar.
//
// ## Invariantes (não quebrar)
//
//  1. **Só mouse.** `pointerType !== "mouse"` sai fora. Toque e caneta já
//     arrastam nativamente, com momentum e snap do sistema; sequestrar isso
//     trocaria um problema por um pior. É também o que dispensa um
//     `matchMedia` de desktop: quem tem mouse recebe, quem não tem, não.
//  2. **Clique continua clique.** Nada é preventado no `pointerdown`; o
//     arraste só começa depois de `DRAG_THRESHOLD_PX` de movimento
//     predominantemente horizontal. Abaixo disso o gesto é um clique comum e o
//     card abre o livro.
//  3. **Depois de arrastar, o clique NÃO dispara.** Soltar o mouse sobre um
//     card ao fim de um arraste abriria o livro sem que ninguém pedisse. Um
//     `click` de captura, one-shot, come o próximo clique — e só ele.
//  4. **Nunca rouba de um controle de formulário.** `input` (incl. `range`),
//     `textarea`, `select` e `contenteditable` tratam o próprio arraste.
//  5. **Opt-out explícito** por `data-no-drag-scroll` no elemento ou em
//     qualquer ancestral até o scroller.
//  6. **`dragstart` é cancelado enquanto há candidato.** Capa de livro é
//     `<img>`, e `<img>` é nativamente arrastável: sem isto o browser começa
//     um drag de imagem no primeiro pixel, os `pointermove` param de chegar e
//     o rail trava — justamente na área que ocupa quase todo o card.
//  7. **`scroll-snap-type` é suspenso DURANTE o arraste** e devolvido ao
//     soltar. Com `snap-mandatory` o browser re-encaixa a cada escrita em
//     `scrollLeft`: escrever 20px devolve 0, a leitura seguinte parte de 0 de
//     novo e o rail fica **preso em zero para sempre**. Medido em Chromium num
//     trilho de passo 176px — 12 amostras de arraste, todas 0. Isso valia para
//     Loja, card da Lura, carrosséis editoriais e o trilho de formatos do
//     Studio, que é onde o snap é parte do desenho. Devolver a propriedade ao
//     soltar é também o que dá o assentamento: o browser encaixa sozinho no
//     item mais próximo.
// ---------------------------------------------------------------------------

/** Movimento a partir do qual o gesto deixa de ser clique e vira arraste. */
const DRAG_THRESHOLD_PX = 4;

/** Marcador de opt-out — ver invariante 5. */
export const NO_DRAG_SCROLL_ATTR = "data-no-drag-scroll";

const FORM_CONTROLS = "input, textarea, select, [contenteditable=''], [contenteditable='true']";

/**
 * Sobe a árvore procurando o primeiro elemento que ROLA na horizontal.
 *
 * A checagem é dupla de propósito: `overflow-x` computado permite rolar, e
 * `scrollWidth > clientWidth` diz que há o que rolar. Sem a segunda, um rail
 * com poucos itens (que cabe inteiro) capturaria o arraste para não fazer
 * nada — e o clique seria comido pela invariante 3 sem contrapartida.
 *
 * ⚠️ A tolerância de 1px não é decorativa: `clientWidth` é inteiro e
 * `scrollWidth` arredonda para cima, então um scroller sem overflow real
 * aparece com 1px de diferença em larguras fracionárias — que é o caso comum
 * dentro do frame de 430px centrado por `translate(-50%)`.
 */
function findHorizontalScroller(start: Element | null, root: Element): HTMLElement | null {
  let el: Element | null = start;

  while (el) {
    if (el instanceof HTMLElement) {
      if (el.hasAttribute(NO_DRAG_SCROLL_ATTR)) return null;

      const overflowX = getComputedStyle(el).overflowX;
      if (
        (overflowX === "auto" || overflowX === "scroll") &&
        el.scrollWidth - el.clientWidth > 1
      ) {
        return el;
      }
    }
    if (el === root) return null;
    el = el.parentElement;
  }

  return null;
}

/**
 * Instala o comportamento. Idempotente por chamador: devolve a função de
 * limpeza, e chamar duas vezes instala dois — quem chama é um efeito único.
 */
export function installDragScroll(root: HTMLElement | Document = document): () => void {
  const doc = root instanceof Document ? root : root.ownerDocument;
  const win = doc.defaultView;
  if (!win) return () => {};

  const scope = root instanceof Document ? doc.documentElement : root;

  let scroller: HTMLElement | null = null;
  let pointerId = -1;
  let startX = 0;
  let startY = 0;
  let lastX = 0;
  let dragging = false;
  /** `user-select` original do body, restaurado ao soltar. */
  let prevUserSelect = "";
  let prevCursor = "";
  /** `scroll-snap-type` inline original do scroller — invariante 7. */
  let prevSnapType = "";

  const reset = () => {
    if (dragging) {
      doc.body.style.userSelect = prevUserSelect;
      doc.body.style.cursor = prevCursor;
      // Devolver a propriedade faz o browser assentar no item mais próximo.
      if (scroller) scroller.style.scrollSnapType = prevSnapType;
    }
    scroller = null;
    pointerId = -1;
    dragging = false;
  };

  const onPointerDown = (event: Event) => {
    const e = event as PointerEvent;

    // Invariante 1 — só mouse. Invariante: só o botão principal.
    if (e.pointerType !== "mouse" || e.button !== 0) return;

    const target = e.target;
    if (!(target instanceof Element)) return;
    if (target.closest(FORM_CONTROLS)) return; // invariante 4

    const found = findHorizontalScroller(target, scope);
    if (!found) return;

    scroller = found;
    pointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    lastX = e.clientX;
    dragging = false;
    // Nada de preventDefault aqui — invariante 2.
  };

  const onPointerMove = (event: Event) => {
    const e = event as PointerEvent;
    if (!scroller || e.pointerId !== pointerId) return;

    if (!dragging) {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      // Predominantemente horizontal: um arraste vertical dentro do rail não é
      // uma tentativa de navegar o rail.
      if (Math.abs(dx) < DRAG_THRESHOLD_PX || Math.abs(dx) <= Math.abs(dy)) return;

      dragging = true;
      prevUserSelect = doc.body.style.userSelect;
      prevCursor = doc.body.style.cursor;
      prevSnapType = scroller.style.scrollSnapType;
      // Sem isto o arraste seleciona o título dos cards no caminho.
      doc.body.style.userSelect = "none";
      doc.body.style.cursor = "grabbing";
      // Invariante 7 — sem isto, rail com snap obrigatório não sai do lugar.
      scroller.style.scrollSnapType = "none";
      win.getSelection?.()?.removeAllRanges();
    }

    scroller.scrollLeft -= e.clientX - lastX;
    lastX = e.clientX;
    e.preventDefault();
  };

  const swallowClick = (e: Event) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const onPointerUp = (event: Event) => {
    const e = event as PointerEvent;
    if (!scroller || e.pointerId !== pointerId) return;

    if (dragging) {
      // Invariante 3 — come EXATAMENTE o clique que este arraste geraria.
      // `capture` para chegar antes do handler do card; `once` para não
      // sobrar armado para o próximo clique de verdade.
      win.addEventListener("click", swallowClick, { capture: true, once: true });
    }
    reset();
  };

  /** Invariante 6 — ver cabeçalho. */
  const onDragStart = (e: Event) => {
    if (scroller) e.preventDefault();
  };

  const onPointerCancel = (event: Event) => {
    const e = event as PointerEvent;
    if (!scroller || e.pointerId !== pointerId) return;
    reset();
  };

  // `capture` no pointerdown para que um `stopPropagation` de algum card não
  // esconda o gesto do módulo. Como o handler não preventa nada, estar na
  // captura é inofensivo.
  scope.addEventListener("pointerdown", onPointerDown, { capture: true });
  scope.addEventListener("dragstart", onDragStart, { capture: true });
  win.addEventListener("pointermove", onPointerMove);
  win.addEventListener("pointerup", onPointerUp);
  win.addEventListener("pointercancel", onPointerCancel);

  return () => {
    scope.removeEventListener("pointerdown", onPointerDown, { capture: true });
    scope.removeEventListener("dragstart", onDragStart, { capture: true });
    win.removeEventListener("pointermove", onPointerMove);
    win.removeEventListener("pointerup", onPointerUp);
    win.removeEventListener("pointercancel", onPointerCancel);
    win.removeEventListener("click", swallowClick, { capture: true });
    reset();
  };
}
